import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * WinMaestros header cart badge counts product sets, not nested poker-chip SKUs.
 * The cart AJAX response is the source of truth for all add/remove/quantity changes.
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];
  #countRequest = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.refreshSetCount(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    this.#countRequest += 1;
  }

  onPageShow = () => {
    this.refreshSetCount(false);
  };

  onCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;
    this.refreshSetCount(true);
  };

  /**
   * The grouped parent line is one set (or its Shopify quantity).
   * Nested denomination variants are components, not additional sets.
   * Ordinary non-nested cart products remain countable.
   */
  countProductSets(items = []) {
    return items.reduce((count, line) => {
      const isNestedComponent =
        line.properties?._wm_native_component === 'yes' ||
        Boolean(line.parent_relationship?.parent_key);
      if (isNestedComponent) return count;
      return count + Math.max(0, Number(line.quantity) || 0);
    }, 0);
  }

  refreshSetCount = async (animate = false) => {
    const request = ++this.#countRequest;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      if (request !== this.#countRequest || !this.isConnected) return;
      const totalSets = this.countProductSets(cart.items || []);
      this.renderCartBubble(totalSets, animate);
    } catch (error) {
      // Keep the server-rendered count if the network is unavailable.
      console.warn('Unable to update WinMaestros set count', error);
    }
  };

  renderCartBubble = async (totalSets, animate = false) => {
    const hasCart = totalSets > 0;
    this.refs.cartBubbleCount.textContent = totalSets > 99 ? '99+' : String(totalSets);
    this.refs.cartBubbleCount.classList.toggle('hidden', !hasCart);
    this.refs.cartBubble.classList.toggle('visually-hidden', !hasCart);
    this.classList.toggle('header-actions__cart-icon--has-cart', hasCart);

    const accessibleText = this.refs.cartBubbleText.querySelector('[data-wm-cart-accessibility-count]');
    if (accessibleText) {
      accessibleText.textContent = 'Cart: ' + totalSets + (totalSets === 1 ? ' set' : ' sets');
    }

    this.refs.cartBubble.classList.toggle('cart-bubble--animating', hasCart && animate);
    if (!hasCart || !animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
